import { Head, Link } from "@inertiajs/react";
import {
  ArrowUpRight,
  BarChart3,
  CalendarCheck,
  CalendarDays,
  Clock,
  ExternalLink,
  MapPin,
  Sparkles,
} from "lucide-react";

import PublicHeader from "@/components/public-header";
import { SiteFooter } from "@/components/site-footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const HIGHLIGHT_STATS = [
  {
    label: "Monthly organic visits",
    value: "96.2K (Ahrefs)",
    icon: CalendarDays,
  },
  { label: "Potential ticket uplift", value: "750 per month", icon: Sparkles },
  { label: "Annual revenue gain", value: "$225,000", icon: BarChart3 },
];

const OG_IMAGE_EXAMPLES = [
  {
    title: "Home page hero",
    description: "Highlights the seasonal highlights, on-brand typography, and automatically includes the logo.",
    imageUrl: "/case-studies/bbg/bbg-after.png",
  },
  {
    title: "Members' Fall Mornings event",
    description:
      "Layers the event name, image, dates, and location, plus the logo for a more personalized feel and increased engagement.",
    imageUrl: "/case-studies/bbg/bbg-visitors-fall-after.png",
  },
  {
    title: "Outdoor Bonsai Display event",
    description:
      "Brings the intimacy of the bonsai centenary anniversary event into the feed with the event name, image, dates, and location, plus the logo for a more personalized feel and increased engagement.",
    imageUrl: "/case-studies/bbg/bbg-bonsai-event-after.png",
  },
];

const REMAKE_PILLARS = [
  {
    title: "Context-rich overlays",
    description:
      "Injected event name, dates, timing, and location directly into the artwork so every share acts like a mini invitation.",
  },
  {
    title: "Brand-consistent templates",
    description:
      "Matched Brooklyn Botanic Garden’s calming palette, typography, and photography style inside OG Pilot templates.",
  },
  {
    title: "Channel tuned variants",
    description:
      "Delivered platform-specific crops and copy callouts for Instagram, Facebook, and partner newsletters.",
  },
];

export default function EventLaunchPodsCaseStudy() {
  return (
    <>
      <Head title="Brooklyn Botanic Garden OG Image Remake Case Study" />

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-100/40 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
        <div className="container mx-auto max-w-6xl px-4 pt-10 pb-24 sm:px-6 lg:px-8">
          <PublicHeader />

          <article className="mt-16 space-y-16">
            <header className="relative overflow-hidden rounded-3xl bg-white/95 p-10 shadow-xl dark:bg-gray-900/90">
              <div
                className="pointer-events-none absolute inset-0 bg-gradient-to-br from-orange-200/60 via-transparent to-transparent"
                aria-hidden="true"
              />
              <div className="relative grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
                <div className="space-y-6">
                  <Badge className="bg-orange-500/10 text-orange-700 dark:bg-orange-500/20 dark:text-orange-200">
                    Case Study · Brooklyn Botanic Garden
                  </Badge>
                  <h1 className="text-4xl font-bold text-gray-900 md:text-5xl dark:text-white">
                    How OG Pilot event remakes could help Brooklyn Botanic Garden unlock $225K in ticket revenue
                  </h1>
                  <p className="text-lg leading-relaxed text-gray-600 dark:text-gray-300">
                    Day #9 of <span className="font-medium">#100DaysOfOGRemakes</span> explored how Brooklyn Botanic
                    Garden’s serene events might convert better with richer OG previews. These OG Pilot concepts add
                    on-brand visuals, date/location overlays, and CTA prompts that could earn 3× more clicks.
                  </p>
                  <div className="flex flex-wrap gap-3 text-sm text-gray-500 dark:text-gray-400">
                    <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 shadow-sm dark:bg-gray-800">
                      <CalendarCheck className="h-4 w-4 text-orange-500" />
                      Published:{" "}
                      <a
                        href="https://x.com/RaulOnRails/status/1978092468479643900"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-orange-700 hover:underline dark:text-orange-300"
                      >
                        Day 9 · #100DaysOfOGRemakes
                      </a>
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 shadow-sm dark:bg-gray-800">
                      <Sparkles className="h-4 w-4 text-orange-500" />
                      OG Pilot remake: Event-driven social images
                    </span>
                  </div>
                </div>

                <div className="grid gap-4 rounded-3xl border border-orange-200/60 bg-orange-100/60 p-6 shadow-inner dark:border-orange-500/30 dark:bg-orange-500/10">
                  {HIGHLIGHT_STATS.map(({ label, value, icon: Icon }) => (
                    <div
                      key={label}
                      className="flex items-start gap-4 rounded-2xl bg-white/90 p-4 shadow dark:bg-gray-950/70"
                    >
                      <div className="rounded-xl bg-orange-500/10 p-3 text-orange-600 dark:bg-orange-500/20 dark:text-orange-200">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold tracking-wide text-gray-500 uppercase dark:text-gray-400">
                          {label}
                        </p>
                        <p className="text-xl font-semibold text-gray-900 dark:text-white">{value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </header>

            <section className="grid gap-10 rounded-3xl bg-white/95 p-10 shadow-xl md:grid-cols-[0.85fr_1.15fr] md:items-center dark:bg-gray-900/90">
              <div className="space-y-5">
                <Badge className="bg-orange-500/10 text-orange-700 dark:bg-orange-500/20 dark:text-orange-200">
                  Baseline snapshot
                </Badge>
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                  The problem: tranquil events, silent previews
                </h2>
                <p className="text-base leading-relaxed text-gray-600 dark:text-gray-300">
                  Brooklyn Botanic Garden’s event pages set a mood—bonsai workshops, fall foliage mornings, lightscape
                  walks. But when those pages hit social feeds, the OG images were static snapshots without context.
                  Audiences had no dates, times, or urgency. Scroll. Scroll. Skip.
                </p>
                <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                  <li>• Generic images omit the event name and timing, so shares feel like random garden photos.</li>
                  <li>• No location cues, leaving out-of-town visitors guessing if the event is even in Brooklyn.</li>
                  <li>• Missing CTAs and limited campaign branding flatten excitement and urgency to buy tickets.</li>
                </ul>
              </div>
              <Link
                href="https://x.com/RaulOnRails/status/1978092468479643900"
                target="_blank"
                rel="noopener noreferrer"
                className="block transition-transform hover:scale-[1.02] focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2 focus-visible:outline-none"
              >
                <blockquote className="rounded-3xl border border-orange-200/70 bg-orange-100/60 p-8 text-lg font-medium text-orange-800 shadow-inner dark:border-orange-500/30 dark:bg-orange-500/10 dark:text-orange-100">
                  <p>
                    “Brooklyn Botanic Garden’s event pages are gateways to serene escapes. But their OG images? Just
                    static snapshots that leave users guessing. The remade previews overlay title, date, and
                    location—every share becomes a mini invite to book tickets.”
                  </p>
                  <footer className="mt-4 text-sm text-orange-700/80 dark:text-orange-200/80">
                    — Day #9 · #100DaysOfOGRemakes
                  </footer>
                </blockquote>
              </Link>
            </section>

            <section className="rounded-3xl bg-white/95 p-10 shadow-xl dark:bg-gray-900/90">
              <div className="mb-6 flex flex-wrap items-start justify-between gap-6">
                <div>
                  <Badge className="bg-orange-500/10 text-orange-700 dark:bg-orange-500/20 dark:text-orange-200">
                    OG image gallery
                  </Badge>
                  <h2 className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                    Download the concept Brooklyn Botanic Garden OG Pilot images
                  </h2>
                  <p className="mt-3 max-w-2xl text-base leading-relaxed text-gray-600 dark:text-gray-300">
                    These sample previews were generated inside OG Pilot as an exploratory remake. Duplicate the
                    template, map your metadata, and go live across every channel without waiting on a custom design
                    sprint.
                  </p>
                </div>
              </div>
              <div className="grid gap-8 md:grid-cols-3">
                {OG_IMAGE_EXAMPLES.map((item) => (
                  <div
                    key={item.title}
                    className="flex flex-col gap-4 rounded-3xl border border-orange-200/60 bg-white/90 p-4 shadow-sm dark:border-orange-500/30 dark:bg-gray-950/70"
                  >
                    <div className="overflow-hidden rounded-2xl border border-orange-200/40 bg-gray-100 shadow-inner dark:border-orange-500/20 dark:bg-gray-900/80">
                      <img
                        src={item.imageUrl}
                        alt={item.title}
                        className="h-auto w-full rounded-2xl object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{item.title}</h3>
                      <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{item.description}</p>
                    </div>
                    <a
                      href={item.imageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-semibold text-orange-600 transition-colors hover:text-orange-700 dark:text-orange-300 dark:hover:text-orange-200"
                    >
                      Open image <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-3xl bg-white/95 p-10 shadow-xl dark:bg-gray-900/90">
              <div className="mb-8 flex flex-wrap items-start justify-between gap-6">
                <div className="max-w-3xl space-y-4">
                  <Badge className="bg-orange-500/10 text-orange-700 dark:bg-orange-500/20 dark:text-orange-200">
                    The OG Pilot remake
                  </Badge>
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                    What could change if OG Pilot guided the garden
                  </h2>
                  <p className="text-base leading-relaxed text-gray-600 dark:text-gray-300">
                    By ingesting Brooklyn Botanic Garden’s visual language into OG Pilot, we prototyped templates that
                    auto-fill event metadata. In practice, each share could extend the garden experience, drawing
                    visitors into Lightscape, Bonsai classes, and seasonal moments with instant clarity.
                  </p>
                </div>
                <Button
                  asChild
                  variant="outline"
                  className="border-orange-300 text-orange-600 hover:border-orange-400 hover:bg-orange-50 dark:border-orange-500/40 dark:text-orange-200 dark:hover:border-orange-400 dark:hover:bg-orange-500/10"
                >
                  <Link href="/sign_up">
                    Try OG Pilot for your events
                    <ArrowUpRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>

              <div className="grid gap-6 md:grid-cols-3">
                {REMAKE_PILLARS.map((pillar) => (
                  <div
                    key={pillar.title}
                    className="rounded-2xl border border-orange-200/60 bg-white/90 p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-md dark:border-orange-500/30 dark:bg-gray-950/80"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{pillar.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-300">{pillar.description}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="grid gap-10 rounded-3xl bg-white/95 p-10 shadow-xl lg:grid-cols-2 dark:bg-gray-900/90">
              <div className="space-y-5">
                <Badge className="bg-orange-500/10 text-orange-700 dark:bg-orange-500/20 dark:text-orange-200">
                  Revenue impact modeling
                </Badge>
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                  Modeled path to a $225K annual uplift
                </h2>
                <p className="text-base leading-relaxed text-gray-600 dark:text-gray-300">
                  Drawing on Ahrefs data and tourism benchmarks, we modeled how OG Pilot’s enriched previews could
                  transform curiosity into booked tickets:
                </p>
                <ul className="space-y-3 text-sm text-gray-700 dark:text-gray-200">
                  <li>• 96.2K monthly organic visits valued at $67K → estimated 120K visitors overall (modeled).</li>
                  <li>
                    • Event pages may drive ~12K social shares. OG Pilot previews are modeled at a 3× click boost.
                  </li>
                  <li>• Conversion lift assumption: 1.5% → 2.5% once share context improves.</li>
                  <li>• Result: +30K visits/month → 750 potential extra ticket sales at $25 average price.</li>
                  <li>
                    • That’s <strong>$18,750 per month</strong> — or <strong>$225K per year</strong> if the benchmarks
                    hold.
                  </li>
                </ul>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  For cultural institutions, every share counts. OG Pilot can ensure each preview carries the full
                  invitation—no copy/paste required—so this upside stays within reach.
                </p>
              </div>
              <div className="flex h-full flex-col justify-center gap-4 rounded-3xl border border-orange-200/60 bg-gradient-to-br from-orange-100 via-white to-orange-50 p-8 shadow-inner dark:border-orange-500/30 dark:from-orange-500/20 dark:via-gray-950 dark:to-orange-500/10">
                <div className="flex items-center gap-3 text-orange-700 dark:text-orange-200">
                  <MapPin className="h-5 w-5" />
                  <span className="text-sm font-medium tracking-wide uppercase">Prospect Park · Brooklyn, NY</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Every share becomes an RSVP</h3>
                <p className="text-sm leading-6 text-gray-600 dark:text-gray-300">
                  OG Pilot keeps event details synced across every social platform. Teams can trigger crawls ahead of
                  each new exhibit, light show, or member morning to keep previews fresh and on-brand.
                </p>
              </div>
            </section>

            <section className="overflow-hidden rounded-3xl bg-gradient-to-r from-orange-500 via-orange-600 to-rose-500 p-10 text-white shadow-xl">
              <div className="grid gap-10 md:grid-cols-[1.1fr_0.9fr] md:items-center">
                <div className="space-y-4">
                  <h2 className="text-3xl font-bold">Turn your next event share into a sold-out experience</h2>
                  <p className="text-sm leading-6 text-orange-50">
                    Connect your domain, generate branded OG images, and keep every event page up to date as programming
                    changes. OG Pilot handles the previews so your team can focus on unforgettable experiences.
                  </p>
                  <div className="flex flex-wrap gap-3 text-sm">
                    <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2">
                      <Clock className="h-4 w-4" />
                      Custom crawls before every event
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2">
                      <BarChart3 className="h-4 w-4" />
                      Analytics-ready reports
                    </span>
                  </div>
                </div>
                <div className="rounded-3xl bg-white/10 p-8 backdrop-blur">
                  <h3 className="text-xl font-semibold">Ready to pilot OG Pilot?</h3>
                  <p className="mt-3 text-sm text-orange-50">
                    Start your free trial, import your brand system, and launch event previews that earn their clicks.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <Button asChild size="lg" className="bg-white text-orange-600 hover:bg-orange-50">
                      <Link href="/sign_up">Start generating images</Link>
                    </Button>
                    <Button
                      asChild
                      size="lg"
                      variant="outline"
                      className="border-white/60 bg-transparent text-white hover:bg-white/10"
                    >
                      <Link href="/#pricing">View agency plans</Link>
                    </Button>
                  </div>
                  <p className="mt-4 text-xs text-orange-100/90">
                    Framework guides:{" "}
                    <Link href="/nextjs-og-image-generator" className="underline hover:text-white">
                      Next.js
                    </Link>
                    {" · "}
                    <Link href="/rails-og-image-generator" className="underline hover:text-white">
                      Rails
                    </Link>
                    {" · "}
                    <Link href="/astro-og-image-generator" className="underline hover:text-white">
                      Astro
                    </Link>
                  </p>
                </div>
              </div>
            </section>
          </article>
        </div>
      </div>
      <SiteFooter />
    </>
  );
}
